import React from 'react';
import { Users, Calendar, UserPlus, GraduationCap } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ModernModuleDashboard from '../../shared/components/dashboard/ModernModuleDashboard';
import {
  buildDashboardCharts,
  pipelineFromKpis,
  sumRowValues,
  withKpiVariants,
} from '../../shared/components/dashboard/moduleDashboardCharts';

export default function HrDashboardPage() {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.ressources_humaines.pages.dashboardPage.${key}`, opts);

  return (
    <ModernModuleDashboard
      apiEndpoint="/hr/dashboard"
      title={p('title', { defaultValue: 'لوحة تحكم الموارد البشرية' })}
      subtitle={p('subtitle', { defaultValue: 'مؤشرات الموظفين والتوظيف والتدريب' })}
      icon={Users}
      accent="from-rose-600 to-pink-900"
      recentTitle={p('recent_hires', { defaultValue: 'آخر التعيينات' })}
      quickLinks={[
        { label: p('link_employees', { defaultValue: 'الموظفون' }), href: '/employees', icon: Users },
        { label: p('link_recruitment', { defaultValue: 'التوظيف' }), href: '/hr/recruitment', icon: UserPlus },
        { label: p('link_leaves', { defaultValue: 'الإجازات' }), href: '/hr/leaves', icon: Calendar },
        { label: p('link_analytics', { defaultValue: 'التحليلات' }), href: '/hr/analyses-rapports', icon: GraduationCap },
      ]}
      mapData={(data) => {
        const kpis = data.kpis || {};
        const chartRows = (data.chartRows || []).map((row) => ({
          label: row.label || row.name || 'غير محدد',
          value: Number(row.value ?? row.count ?? 0),
        }));

        return {
          kpis: withKpiVariants([
            { label: p('kpi_employees', { defaultValue: 'إجمالي الموظفين' }), value: kpis.totalEmployees ?? 0, icon: Users },
            { label: p('kpi_on_leave', { defaultValue: 'في إجازة اليوم' }), value: kpis.onLeaveToday ?? 0, icon: Calendar },
            { label: p('kpi_open_positions', { defaultValue: 'وظائف شاغرة' }), value: kpis.openPositions ?? 0, icon: UserPlus },
            { label: p('kpi_training', { defaultValue: 'تدريب نشط' }), value: kpis.trainingActive ?? 0, icon: GraduationCap },
          ]).slice(0, 4),
          charts: buildDashboardCharts({
            pipeline: {
              title: p('chart_pipeline_title', { defaultValue: 'ملخص الموارد البشرية' }),
              subtitle: p('chart_pipeline_subtitle', { defaultValue: 'موظفون وإجازات وتوظيف' }),
              alwaysShow: true,
              rows: pipelineFromKpis([
                { label: p('kpi_employees', { defaultValue: 'إجمالي الموظفين' }), value: kpis.totalEmployees ?? 0 },
                { label: p('kpi_on_leave', { defaultValue: 'في إجازة اليوم' }), value: kpis.onLeaveToday ?? 0 },
                { label: p('kpi_open_positions', { defaultValue: 'وظائف شاغرة' }), value: kpis.openPositions ?? 0 },
              ]),
            },
            status: {
              title: p('chart_status_title', { defaultValue: 'حالة الفريق' }),
              subtitle: p('chart_status_subtitle', { defaultValue: 'حاضرون مقابل في إجازة' }),
              active: Math.max(0, (kpis.totalEmployees ?? 0) - (kpis.onLeaveToday ?? 0)),
              inactive: kpis.onLeaveToday ?? 0,
              alwaysShow: true,
            },
            ranked: {
              title: p('chart_departments', { defaultValue: 'الموظفون حسب القسم' }),
              subtitle: p('chart_departments_subtitle', { defaultValue: 'أكبر الأقسام' }),
              rows: chartRows,
            },
          }),
          recentItems: data.recentItems || [],
        };
      }}
    />
  );
}
